import React, { useEffect, useState } from "react";
import { Alert, Button, Drawer, Space, DatePicker, Input, Form, Select } from "antd";
import axios from "axios";
import { Error, Success } from "../Messages/Messages";
import dayjs from "dayjs";
import GeneralModal from "../../serviceProvider/components/General.modal";
import { sendAppointment } from "./appointmentApi";
import CustomerBranchPicker from "../Billing/CustomerBranchPicker";
import OnTheWayButton from "../../serviceProvider/components/notifications/OnTheWayButton";
import { Typography } from "antd";

import { useTranslation } from "react-i18next";

const toPickerValue = value => (value ? dayjs(value) : null);

const CalendarDrawer = ({
  open,
  onClose,
  drawerData,
  updateParent,
  employeesArray,
  handleCancel,
  wEventclicked
}) => {
  const { t } = useTranslation("dashboard");
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const [branchSel, setBranchSel] = useState({ clientBranch: null });
  // The list endpoint populates the client (with its branches).
  const client = drawerData?.client && drawerData.client._id ? drawerData.client : null;
  const savedBranch = drawerData?.clientBranch || null;

  useEffect(() => {
    setBranchSel({ clientBranch: savedBranch });
  }, [drawerData?._id, savedBranch]);

  useEffect(() => {
    if (drawerData) {
      form.setFieldsValue({
        title: drawerData?.title,
        // Calendar events carry real Date objects; the pickers need dayjs values.
        start: toPickerValue(drawerData?.storedStart ?? drawerData?.start),
        end: toPickerValue(drawerData?.storedEnd ?? drawerData?.end),
        desc: drawerData?.desc,
        // The list endpoint populates employees; the Select works with ids.
        employees: (drawerData?.employees || []).map(e => (e && e._id ? e._id : e))
      });
    }
  }, [drawerData, form]);

  const onSubmit = async values => {
    const formData = {
      ...values,
      start: values.start.toISOString(),
      end: values.end.toISOString()
    };
    if (String(branchSel.clientBranch || "") !== String(savedBranch || ""))
      formData.clientBranch = branchSel.clientBranch || null;

    try {
      const res = await sendAppointment("put", `/update/${drawerData?._id}`, formData, t);
      if (!res) return; // the user kept the conflicts dialog's "Cancel"

      if (res.status === 200 || res.status === 304) {
        Success(res.data.successMessage);
        updateParent();
        onClose();
        handleCancel();
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      console.error(err);
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const deleteHandler = async id => {
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/appointments/delete/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );

      if (res.status === 200 || res.status === 304) {
        Success(res.data.successMessage);
        updateParent();
        onClose();
        handleCancel();
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      console.error(err);
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const { Title } = Typography;

  const onChangetab = key => {};

  const leaveConflicts = drawerData?.leaveConflicts || [];

  const items = (
    <>
      {leaveConflicts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("Employees on leave during this appointment")}
          description={leaveConflicts
            .map(c => `${c.employeeName} (${t(c.leaveType || "On leave")})`)
            .join(", ")}
        />
      )}
      {client && drawerData?._id && (
        <div style={{ marginBottom: 16 }}>
          <OnTheWayButton appointmentId={drawerData._id} block />
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          title: drawerData?.title,
          start: toPickerValue(drawerData?.storedStart ?? drawerData?.start),
          end: toPickerValue(drawerData?.storedEnd ?? drawerData?.end),
          desc: drawerData?.desc
        }}
      >
        <Form.Item
          label={
            <Title level={5} className="modals-inputs-label">
              {t("Event Title")}
            </Title>
          }
          name="title"
          rules={[{ required: true, message: t("Please enter the event title") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label={
            <Title level={5} className="modals-inputs-label">
              {t("Start Date")}
            </Title>
          }
          name="start"
          rules={[{ required: true, message: t("Please select the start date") }]}
        >
          <DatePicker showTime style={{ width: "100%" }} format={"DD/MMM/YYYY HH:mm"} />
        </Form.Item>

        <Form.Item
          label={
            <Title level={5} className="modals-inputs-label">
              {t("End Date")}
            </Title>
          }
          name="end"
          rules={[{ required: true, message: t("Please select the end date") }]}
        >
          <DatePicker showTime style={{ width: "100%" }} format={"DD/MMM/YYYY HH:mm"} />
        </Form.Item>
        <CustomerBranchPicker
          customer={client}
          value={branchSel}
          onChange={setBranchSel}
          showBillTo={false}
        />
        <Form.Item
          label={
            <Title level={5} className="modals-inputs-label">
              {t("Employees")}
            </Title>
          }
          name="employees"
        >
          <Select
            mode="multiple"
            optionFilterProp="label"
            options={employeesArray}
            placeholder={t("Select employees")}
          />
        </Form.Item>
        <Form.Item
          label={
            <Title level={5} className="modals-inputs-label">
              {t("Description")}
            </Title>
          }
          name="desc"
          rules={[{ required: true, message: t("Please provide a description") }]}
        >
          <TextArea rows={4} />
        </Form.Item>
      </Form>
    </>
  );

  return (
    <>
      <GeneralModal
        title={<Title level={4}>{t("Update Appointment")}</Title>}
        onOk={() => form.submit()}
        open={open}
        okText={t("Save")}
        onCancel={handleCancel}
        onChange={onChangetab}
        items={items}
        deleteEvent={() => deleteHandler(drawerData?._id)}
        wEventclicked="updateordelete"
      />
    </>
  );
};

export default CalendarDrawer;
